import { textColorFields } from '../_shared/textColor.js';
import { playButtonField } from '../_shared/videoPlayButton.js';
import { animationFields } from '../_shared/universal.js';
/**
 * slideshow-hero — a 1:1 rebuild of Shrine Pro's "Slideshow hero" section
 * (shrine-pro-ref/sections/slideshow-hero.liquid). A carousel of full-bleed hero SLIDES: each slide is a
 * background image (or uploaded video) — desktop + optional mobile — under a tunable overlay scrim, with an
 * overlay content block (heading prefix, highlight heading, heading suffix, rich text, up to two buttons),
 * freely positioned with the desktop/mobile layout knobs and the 6 Shrine color schemes (five presets +
 * custom palette). Section-level: device VISIBILITY, minimum height (adapt/none/pixels/viewport, desktop &
 * mobile), the SLIDER (slider|fade type, drag, autoplay + speed, dots + dots color scheme) and section
 * padding. The rendered HTML mirrors Shrine's `.hero > .splide > .splide__track > .splide__list > li.splide__slide
 * > .splide__slide__container > .hero-slide.slideshow-hero-slide` tree verbatim; the hero-slide CSS is the
 * ported Shrine base.css (scoped per-instance), typography/spacing use the storefront theme tokens.
 *
 * Field-type mapping (Shopify → ours): inline_richtext→richtext (bold → the single title-highlight color),
 * richtext→richtext, image_picker→image, video→video (an UPLOADED clip — aquilixShop rule: media is
 * uploaded, never a raw URL, see spec 0020), url→link, color→color, range/number/select/checkbox unchanged,
 * header→group, visible_if→visibleWhen. Ids are clean camelCase; labels/options/defaults stay faithful to
 * Shrine.
 *
 * DELIBERATELY DEFERRED (faithful follow-ups, flagged — same discipline as parallax-hero): Shrine's
 * TRANSPARENT-HEADER options + "hide announcement bars" are header-GROUP concerns (they retint the global
 * header and fit the hero under it) that a self-contained section cannot own in this architecture, and the
 * per-slide VIDEO SOUND button (Shrine `display_sound_btn` / `sound_btn_color_scheme`) needs an unmute-toggle
 * engine we do not ship yet (background videos autoplay muted, like every other section) — both are omitted
 * here rather than shipped as dead controls. Also carried-not-yet-live (data-*): autoplay + fade type (dots +
 * swipe work without JS; loop/slider is native scroll-snap).
 */
/** Convenience for the literal hex defaults Shrine ships. */
const hex = (value) => ({ ref: 'custom', value });
const HEIGHT = 'Height';
const SLIDER = 'Slider';
const PADDING = 'Section padding';
// ── The single "Slide" block — Shrine's `slide` block. ─────────────────────────────────────────────────
const slideBlock = {
    type: 'slide',
    name: 'Slide',
    fields: [
        // ── Desktop background ─────────────────────────────────────────────────────
        {
            id: 'desktopBgImage',
            type: 'image',
            label: 'Desktop background image',
            group: 'Desktop background',
        },
        {
            id: 'desktopBgVideo',
            type: 'video',
            label: 'Desktop background video',
            group: 'Desktop background',
            info: 'An uploaded clip (autoplays muted & looped). Takes priority over the desktop background image.',
        },
        // Une seule case pour les DEUX clips de la diapositive (ordinateur + mobile) : c'est le même plan,
        // vu sur deux appareils, et deux interrupteurs pour une seule décision seraient un piège.
        playButtonField({
            defaultOn: false,
            group: 'Desktop background',
            visibleWhen: { field: 'desktopBgVideo', empty: false },
        }),
        {
            id: 'desktopOverlayColor',
            type: 'color',
            label: 'Desktop overlay color',
            group: 'Desktop background',
            default: hex('#000000'),
        },
        {
            id: 'desktopOverlayOpacity',
            type: 'range',
            label: 'Desktop overlay opacity',
            group: 'Desktop background',
            min: 0,
            max: 100,
            step: 1,
            unit: '%',
            default: 0,
        },
        // ── Mobile background ──────────────────────────────────────────────────────
        {
            id: 'mobileBgImage',
            type: 'image',
            label: 'Mobile background image',
            group: 'Mobile background',
        },
        {
            id: 'mobileBgVideo',
            type: 'video',
            label: 'Mobile background video',
            group: 'Mobile background',
            info: 'An uploaded clip (autoplays muted & looped). Takes priority over the mobile background image.',
        },
        {
            id: 'mobileOverlayColor',
            type: 'color',
            label: 'Mobile overlay color',
            group: 'Mobile background',
            default: hex('#000000'),
        },
        {
            id: 'mobileOverlayOpacity',
            type: 'range',
            label: 'Mobile overlay opacity',
            group: 'Mobile background',
            min: 0,
            max: 100,
            step: 1,
            unit: '%',
            default: 0,
        },
        // ── Content ────────────────────────────────────────────────────────────────
        {
            id: 'headingPrefix',
            type: 'text',
            label: 'Heading prefix',
            group: 'Content',
            default: 'Header prefix',
            defaultKey: 'section.header_prefix',
            // Placeholder de CONTENU, pas libellé d'interface — l'IA doit l'écrire (0059).
            aiWritable: true,
        },
        {
            id: 'headingPrefixStyle',
            type: 'select',
            label: 'Heading prefix style',
            group: 'Content',
            default: 'plain',
            options: [
                { value: 'plain', label: 'Plain text' },
                { value: 'chip', label: 'Bordered chip' },
            ],
        },
        {
            id: 'slideLabel',
            type: 'text',
            label: 'Slide label',
            group: 'Content',
            info: 'Short name shown in the "Next:" arrow navigation (when enabled on the section).',
        },
        {
            id: 'heading',
            type: 'richtext',
            label: 'Heading',
            group: 'Content',
            default: { format: 'html', html: '<p>Slideshow hero</p>' },
            info: 'Bold a word (B) to highlight it with the heading highlight color.',
        },
        {
            id: 'titleHighlightColor',
            type: 'color',
            label: 'Heading highlight color',
            group: 'Content',
            default: { ref: 'theme', token: 'accent' },
        },
        {
            id: 'headingSize',
            type: 'select',
            label: 'Heading size',
            group: 'Content',
            default: 'h1',
            options: [
                { value: 'h2', label: 'Small' },
                { value: 'h1', label: 'Medium' },
                { value: 'h0', label: 'Large' },
                { value: 'hxl', label: 'Extra large' },
            ],
        },
        { id: 'headingSuffix', type: 'text', label: 'Heading suffix', group: 'Content' },
        {
            id: 'headingPrefixSize',
            type: 'select',
            label: 'Heading prefix & suffix size',
            group: 'Content',
            default: 'h4',
            options: [
                { value: 'h5', label: 'Extra small' },
                { value: 'h4', label: 'Small' },
                { value: 'h3', label: 'Medium' },
                { value: 'h2', label: 'Large' },
            ],
        },
        {
            id: 'text',
            type: 'richtext',
            label: 'Text',
            group: 'Content',
            default: { format: 'html', html: "<p>Tell your brand's story with a slideshow hero.</p>" },
            defaultKey: 'section.hero_text',
            // Placeholder de CONTENU, pas libellé d'interface — l'IA doit l'écrire (0059).
            aiWritable: true,
        },
        {
            id: 'buttonLabel1',
            type: 'text',
            label: 'First button label',
            group: 'Content',
            default: 'Button label',
            defaultKey: 'section.button_label',
            // Placeholder de CONTENU, pas libellé d'interface — l'IA doit l'écrire (0059).
            aiWritable: true,
            info: 'Leave the label blank to hide the button.',
        },
        { id: 'buttonLink1', type: 'link', label: 'First button link', group: 'Content' },
        {
            id: 'buttonStyleSecondary1',
            type: 'checkbox',
            label: 'Use outline button style',
            group: 'Content',
            default: false,
        },
        {
            id: 'buttonLabel2',
            type: 'text',
            label: 'Second button label',
            group: 'Content',
            info: 'Leave the label blank to hide the button.',
        },
        { id: 'buttonLink2', type: 'link', label: 'Second button link', group: 'Content' },
        {
            id: 'buttonStyleSecondary2',
            type: 'checkbox',
            label: 'Use outline button style',
            group: 'Content',
            default: false,
        },
        {
            // La hauteur des boutons était CODÉE EN DUR (`padding:1.3rem 2.8rem`) : aucun thème ne pouvait la
            // toucher. Réglage GÉNÉRIQUE, pas « pour un thème » — la règle de 0032 est qu'un thème est de la
            // donnée, donc quand une mise en page manque, on ajoute le réglage au composant, jamais un
            // composant au thème. Le défaut vaut exactement l'ancienne valeur en dur (1,3 rem = 21 px), donc
            // aucun thème existant ne bouge d'un pixel.
            id: 'buttonPaddingY',
            type: 'range',
            label: 'Button height',
            group: 'Content',
            min: 10,
            max: 44,
            step: 1,
            unit: 'px',
            default: 21,
            info: 'Vertical padding inside the hero buttons.',
        },
        {
            id: 'colorScheme',
            type: 'select',
            label: 'Color scheme',
            group: 'Content',
            default: 'background-1',
            info: 'The custom color scheme is edited at the bottom of the block settings.',
            options: [
                { value: 'accent-1', label: 'Accent 1' },
                { value: 'accent-2', label: 'Accent 2' },
                { value: 'background-1', label: 'Background 1' },
                { value: 'background-2', label: 'Background 2' },
                { value: 'inverse', label: 'Inverse' },
                { value: 'custom', label: 'Custom' },
            ],
        },
        // ── Desktop content layout ─────────────────────────────────────────────────
        {
            id: 'fullPageWidth',
            type: 'checkbox',
            label: 'Use full page width',
            group: 'Desktop content layout',
            default: false,
        },
        {
            id: 'contentMaxWidth',
            type: 'select',
            label: 'Content maximum width',
            group: 'Desktop content layout',
            default: 'unlimited',
            options: [
                { value: 'unlimited', label: 'Unlimited' },
                { value: 'pixels', label: 'Amount in pixels' },
                { value: 'percentage', label: 'Amount in percentage' },
            ],
        },
        {
            id: 'contentPixelsMaxWidth',
            type: 'number',
            label: 'Content maximum width in pixels',
            group: 'Desktop content layout',
            default: 500,
            visibleWhen: { field: 'contentMaxWidth', equals: 'pixels' },
        },
        {
            id: 'contentPercentageMaxWidth',
            type: 'range',
            label: 'Content maximum width in percentage',
            group: 'Desktop content layout',
            min: 0,
            max: 100,
            step: 5,
            unit: '%',
            default: 50,
            info: 'Relative to the container (the full page if "Use full page width" is checked). Automatically 100% on mobile.',
            visibleWhen: { field: 'contentMaxWidth', equals: 'percentage' },
        },
        {
            id: 'contentVerticalPosition',
            type: 'select',
            label: 'Content vertical position',
            group: 'Desktop content layout',
            default: 'center',
            options: [
                { value: 'flex-start', label: 'Top' },
                { value: 'center', label: 'Center' },
                { value: 'flex-end', label: 'Bottom' },
            ],
        },
        {
            id: 'contentHorizontalPosition',
            type: 'select',
            label: 'Content horizontal position',
            group: 'Desktop content layout',
            default: 'center',
            options: [
                { value: 'flex-start', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'flex-end', label: 'Right' },
            ],
        },
        {
            id: 'contentTextAlignment',
            type: 'select',
            label: 'Content text alignment',
            group: 'Desktop content layout',
            default: 'center',
            options: [
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'right', label: 'Right' },
            ],
        },
        // ── Mobile content layout ──────────────────────────────────────────────────
        {
            id: 'mobileContentVerticalPosition',
            type: 'select',
            label: 'Mobile content vertical position',
            group: 'Mobile content layout',
            default: 'center',
            options: [
                { value: 'flex-start', label: 'Top' },
                { value: 'center', label: 'Center' },
                { value: 'flex-end', label: 'Bottom' },
            ],
        },
        {
            id: 'mobileContentTextAlignment',
            type: 'select',
            label: 'Mobile content text alignment',
            group: 'Mobile content layout',
            default: 'center',
            options: [
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'right', label: 'Right' },
            ],
        },
        // ── Custom color scheme (applied when Color scheme = Custom) ────────────────
        {
            id: 'customColorsText',
            type: 'color',
            label: 'Custom text color',
            group: 'Custom color scheme',
            default: hex('#2E2A39'),
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customColorsSolidButtonBackground',
            type: 'color',
            label: 'Custom solid button background',
            group: 'Custom color scheme',
            default: hex('#dd1d1d'),
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customColorsSolidButtonText',
            type: 'color',
            label: 'Custom solid button label color',
            group: 'Custom color scheme',
            default: hex('#ffffff'),
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customColorsOutlineButton',
            type: 'color',
            label: 'Custom outline button color',
            group: 'Custom color scheme',
            default: hex('#dd1d1d'),
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
    ],
};
export const slideshowHeroSchema = {
    type: 'slideshow-hero',
    name: 'Slideshow hero',
    version: 1,
    templates: ['index', 'product', 'collection', 'page', 'thank-you'],
    fields: [
        // ── (top, ungrouped) ──────────────────────────────────────────────────────
        {
            id: 'visibility',
            type: 'select',
            label: 'Display on',
            default: 'always-display',
            options: [
                { value: 'always-display', label: 'All devices' },
                { value: 'mobile-hidden', label: 'Desktop only' },
                { value: 'desktop-hidden', label: 'Mobile only' },
            ],
        },
        // ── Height ────────────────────────────────────────────────────────────────
        {
            id: 'minDesktopHeightType',
            type: 'select',
            label: 'Minimum desktop height',
            group: HEIGHT,
            default: 'pixels',
            info: '"Adapt to image" uses the first slide\'s desktop image aspect ratio.',
            options: [
                { value: 'adapt', label: 'Adapt to image' },
                { value: 'autofit', label: 'None (fit content)' },
                { value: 'pixels', label: 'Amount in pixels' },
                { value: '100vh', label: 'Viewport height' },
            ],
        },
        {
            id: 'desktopPixelsHeight',
            type: 'number',
            label: 'Desktop minimum height in pixels',
            group: HEIGHT,
            default: 600,
            visibleWhen: { field: 'minDesktopHeightType', equals: 'pixels' },
        },
        {
            id: 'minMobileHeightType',
            type: 'select',
            label: 'Minimum mobile height',
            group: HEIGHT,
            default: 'pixels',
            info: '"Adapt to image" uses the first slide\'s mobile image aspect ratio (or desktop if no mobile image).',
            options: [
                { value: 'adapt', label: 'Adapt to image' },
                { value: 'autofit', label: 'None (fit content)' },
                { value: 'pixels', label: 'Custom amount of pixels' },
                { value: '100vh', label: 'Viewport height' },
            ],
        },
        {
            id: 'mobilePixelsHeight',
            type: 'number',
            label: 'Mobile minimum height in pixels',
            group: HEIGHT,
            default: 400,
            visibleWhen: { field: 'minMobileHeightType', equals: 'pixels' },
        },
        // ── Slider ────────────────────────────────────────────────────────────────
        {
            id: 'sliderType',
            type: 'select',
            label: 'Type',
            group: SLIDER,
            default: 'loop',
            options: [
                { value: 'loop', label: 'Slider' },
                { value: 'fade', label: 'Fade' },
            ],
        },
        { id: 'drag', type: 'checkbox', label: 'Enable drag', group: SLIDER, default: false },
        { id: 'autoplay', type: 'checkbox', label: 'Enable autoplay', group: SLIDER, default: false },
        {
            id: 'autoplaySpeed',
            type: 'range',
            label: 'Autoplay speed',
            group: SLIDER,
            min: 1,
            max: 30,
            step: 1,
            unit: 'sec',
            default: 10,
        },
        { id: 'enableDots', type: 'checkbox', label: 'Enable dots', group: SLIDER, default: true },
        {
            id: 'dotsColorScheme',
            type: 'select',
            label: 'Dots color',
            group: SLIDER,
            default: 'background-1',
            options: [
                { value: 'accent-1', label: 'Accent 1' },
                { value: 'accent-2', label: 'Accent 2' },
                { value: 'background-1', label: 'Background 1' },
                { value: 'background-2', label: 'Background 2' },
                { value: 'inverse', label: 'Text' },
            ],
        },
        { id: 'enableArrows', type: 'checkbox', label: 'Enable arrows', group: SLIDER, default: false },
        {
            id: 'arrowsColorScheme',
            type: 'select',
            label: 'Arrows color',
            group: SLIDER,
            default: 'background-1',
            options: [
                { value: 'accent-1', label: 'Accent 1' },
                { value: 'accent-2', label: 'Accent 2' },
                { value: 'background-1', label: 'Background 1' },
                { value: 'background-2', label: 'Background 2' },
                { value: 'inverse', label: 'Text' },
            ],
            visibleWhen: { field: 'enableArrows', equals: true },
        },
        {
            id: 'showNextLabel',
            type: 'checkbox',
            label: 'Show next-slide label',
            group: SLIDER,
            default: false,
            info: 'Shows "Next: <slide label>" beside the arrows (set each slide\'s label).',
            visibleWhen: { field: 'enableArrows', equals: true },
        },
        // ── Section padding ───────────────────────────────────────────────────────
        {
            // The hero BAND (media + overlay) takes N% of the page width, centred; 100 = full bleed.
            id: 'sectionWidth',
            type: 'range',
            label: 'Section width',
            group: 'Section',
            min: 30,
            max: 100,
            step: 1,
            unit: '%',
            default: 100,
        },
        {
            id: 'paddingTop',
            type: 'range',
            label: 'Top padding',
            group: PADDING,
            min: 0,
            max: 100,
            step: 5,
            unit: 'px',
            default: 50,
        },
        {
            id: 'paddingBottom',
            type: 'range',
            label: 'Bottom padding',
            group: PADDING,
            min: 0,
            max: 100,
            step: 5,
            unit: 'px',
            default: 50,
        },
        // Text colors — the seller's own colour/gradient for this band's headings and body text.
        ...textColorFields(),
        // The universal Animation group (2026-09-18): this section writes its own section controls
        // instead of spreading `universalFields`, so the group is spread in on its own.
        ...animationFields,
    ],
    blocks: [slideBlock],
    maxBlocks: 12,
};
export const slideshowHeroPresets = [
    {
        name: 'Slideshow hero',
        settings: {
            visibility: 'always-display',
            minDesktopHeightType: 'pixels',
            desktopPixelsHeight: 600,
            minMobileHeightType: 'pixels',
            mobilePixelsHeight: 400,
            sliderType: 'loop',
            drag: false,
            autoplay: false,
            autoplaySpeed: 10,
            enableDots: true,
            dotsColorScheme: 'background-1',
            paddingTop: 50,
            paddingBottom: 50,
        },
        blocks: [{ type: 'slide' }],
    },
];
/**
 * How this section presents itself in "Add section": label, group, icon.
 *
 * It lives beside the schema, NOT in `index.ts`, and that is the point. `index.ts` is the module that pulls
 * in `Component.tsx`, and the builder reads `meta` for all sixty-two sections to paint the picker while
 * rendering only the handful a page actually uses. Keeping `meta` here lets `registry.lazy.gen.ts` import it
 * without dragging every section's render code into the editor's entry chunk.
 */
export const slideshowHeroMeta = {
    label: 'Slideshow hero',
    group: 'Layout',
    icon: 'layout',
};
//# sourceMappingURL=schema.js.map